// Fix 23 (Schedule polish) — pure-JS helpers for weekday/month/city
// localization.  All lookups are static; no external service, no
// browser Intl fallback for weekdays (we compute them from the ISO
// date directly so the result is independent of the client timezone).

const MONTHS_EN = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN",
                   "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
                   "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];

const WEEKDAYS_EN = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const WEEKDAYS_AR = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

// Complete scoped Arabic city map — matches spec verbatim.
export const CITY_AR = {
  "dubai":         "دبي",
  "manama":        "المنامة",
  "jeddah":        "جدة",
  "riyadh":        "الرياض",
  "abu dhabi":     "أبوظبي",
  "doha":          "الدوحة",
  "kuwait city":   "مدينة الكويت",
  "beirut":        "بيروت",
  "amman":         "عمان",
  "cairo":         "القاهرة",
  "muscat":        "مسقط",
};

/** Zeller-safe weekday for an ISO YYYY-MM-DD string.  Never uses the
 *  browser timezone.  Returns 0..6 where 0=Sunday. */
function _weekdayIndex(iso) {
  if (!iso || typeof iso !== "string") return 0;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return 0;
  let y = +m[1], mo = +m[2], d = +m[3];
  // Sakamoto's algorithm — returns 0=Sunday.
  const t = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];
  if (mo < 3) y -= 1;
  return (y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) + t[mo - 1] + d) % 7;
}

/** Return the weekday label for a stored ISO date.  Purely
 *  date-driven — client clock and timezone are irrelevant. */
export function weekdayLabel(iso, locale = "en") {
  const idx = _weekdayIndex(iso);
  const table = locale.startsWith("ar") ? WEEKDAYS_AR : WEEKDAYS_EN;
  return table[idx] || "";
}

/** Return the 3-letter English month or full Arabic month for an ISO
 *  date's month component. */
export function monthLabel(iso, locale = "en") {
  if (!iso) return "";
  const m = iso.match(/^\d{4}-(\d{2})/);
  const mm = m ? Math.max(1, Math.min(12, +m[1])) : 1;
  return (locale.startsWith("ar") ? MONTHS_AR : MONTHS_EN)[mm - 1];
}

/** Return the month index (1..12) directly from an ISO date. */
export function monthIndex(iso) {
  const m = iso && iso.match(/^\d{4}-(\d{2})/);
  return m ? Math.max(1, Math.min(12, +m[1])) : 1;
}

/** Scoped Arabic city lookup.  Falls back to the raw stored value
 *  (unchanged) for any city not in the whitelist. */
export function cityLabel(city, locale = "en") {
  if (!locale.startsWith("ar")) return city || "";
  const key = String(city || "").trim().toLowerCase();
  return CITY_AR[key] || city || "";
}

/** Full Arabic month label for the current-month heading. */
export function monthLabelFromIndex(index, locale = "en") {
  const idx = Math.max(1, Math.min(12, index || 1)) - 1;
  return (locale.startsWith("ar") ? MONTHS_AR : MONTHS_EN)[idx];
}
